import React, { useState, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../utils/api';

export default function ProfilePanel({ user }) {
  const { updateUser } = useAuth();
  const fileRef = useRef();
  
  const [form, setForm] = useState({
    name:         user?.name         || '',
    email:        user?.email        || '',
    phone:        user?.phone        || '',
    businessName: user?.businessName || '',
    address:      user?.address      || '',
    city:         user?.city         || '',
    state:        user?.state        || '',
    pincode:      user?.pincode      || '',
  });
  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError]     = useState('');
  const [uploadingImg, setUploadingImg] = useState(false);
  const [previewImg, setPreviewImg] = useState(user?.profile_image || null);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setSaving(true); setSuccess(''); setError('');
    try {
      await api.put('/api/butcher/profile', form);
      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // ── Upload profile image ──────────────────────────────────────
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB.');
      return;
    }

    setPreviewImg(URL.createObjectURL(file));
    setUploadingImg(true);
    setError('');
    
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await api.put('/api/butcher/profile-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateUser?.({ ...user, profile_image: data.profile_image });
      setSuccess('Profile photo updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed.');
      setPreviewImg(user?.profile_image || null);
    } finally {
      setUploadingImg(false);
    }
  };

  const inputClass = "w-full px-3 py-2 bg-surface-container-low border border-outline-variant/30 rounded-lg text-sm focus:ring-1 focus:ring-primary outline-none";
  const labelClass = "text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1";

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-outline-variant/10">

        {/* Avatar with Upload */}
        <div className="flex items-center gap-5 mb-6 pb-6 border-b border-surface-container-high">
          <div 
            className="relative flex-shrink-0 group cursor-pointer"
            onClick={() => fileRef.current?.click()}
          >
            {previewImg ? (
              <img 
                src={previewImg} 
                alt="Profile"
                className="w-16 h-16 rounded-full object-cover border-4 border-red-100"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center
                              text-white font-black text-2xl border-4 border-red-100">
                {(user?.name || 'B')[0].toUpperCase()}
              </div>
            )}
            
            {/* Hover overlay */}
            <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100
                            transition-opacity flex items-center justify-center">
              {uploadingImg ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="material-symbols-outlined text-white text-[18px]">photo_camera</span>
              )}
            </div>
            
            <input 
              ref={fileRef}
              type="file" 
              accept="image/*"
              className="hidden" 
              onChange={handleImageChange} 
            />
          </div>
          
          <div>
            <p className="font-black text-lg">{user?.name || 'Butcher'}</p>
            <p className="text-sm text-secondary">{user?.email}</p>
            <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 mt-1 inline-block">
              🔪 Butcher Account
            </span>
          </div>
        </div>

        {success && (
          <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
            <span className="material-symbols-outlined text-base">check_circle</span>{success}
          </div>
        )}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
            <span className="material-symbols-outlined text-base">error</span>{error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Full Name</label>
            <input name="name" value={form.name} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Email</label>
            <input name="email" value={form.email} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Phone</label>
            <input name="phone" value={form.phone} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Business / Shop Name</label>
            <input name="businessName" value={form.businessName} onChange={handleChange} className={inputClass} placeholder="e.g. Al-Noor Meat Shop" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Address</label>
            <textarea name="address" value={form.address} onChange={handleChange} rows={2} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>City</label>
            <input name="city" value={form.city} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>State</label>
            <input name="state" value={form.state} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Pincode</label>
            <input name="pincode" value={form.pincode} onChange={handleChange} className={inputClass} />
          </div>
        </div>

        <button onClick={handleSave} disabled={saving}
          className="mt-6 w-full py-2.5 rounded-xl bg-primary text-white font-bold text-sm hover:opacity-90 transition-all disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
}